import React, { useState, useEffect, useRef } from 'react';
import { 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  ChevronLeft, 
  ChevronRight, 
  Bookmark, 
  Flag, 
  FileText, 
  Sparkles, 
  X, 
  Layers, 
  ArrowLeft, 
  ShieldAlert, 
  HelpCircle, 
  PenTool, 
  Send,
  Timer,
  Check,
  RotateCcw,
  Lock
} from 'lucide-react';
import { 
  MockExam, 
  MockAttempt, 
  Question, 
  ConstructedResponsePrompt, 
  UserProfile,
  UnofficialScoreReport 
} from '../types';
import { 
  loadMockExamContent, 
  createMockAttempt, 
  updateMockAttempt, 
  MockLoadResult,
  generateUnofficialScoreReport 
} from '../lib/mockService';
import { isPaperUnlockedForUser } from '../lib/firebase';
import { UnofficialScoreReportView } from './UnofficialScoreReportView';

interface MockExamSimulatorProps {
  mockExam: MockExam;
  userProfile: UserProfile;
  onExit: () => void;
  openPricingModal?: () => void;
}

export const MockExamSimulator: React.FC<MockExamSimulatorProps> = ({
  mockExam,
  userProfile,
  onExit,
  openPricingModal
}) => {
  // Loading & Data state
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'data_error'>('loading');
  const [dataErrorDetail, setDataErrorDetail] = useState<{
    missingId: string;
    itemType: 'question' | 'constructed_response' | 'mock_exam';
    message: string;
  } | null>(null);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [crPrompts, setCrPrompts] = useState<ConstructedResponsePrompt[]>([]);
  const [attemptId, setAttemptId] = useState<string>('');

  // Session state
  const [activeSection, setActiveSection] = useState<'mc' | 'cr'>('mc');
  const [currentMcIndex, setCurrentMcIndex] = useState<number>(0);
  const [currentCrIndex, setCurrentCrIndex] = useState<number>(0);

  // User responses
  const [userAnswers, setUserAnswers] = useState<Record<string, number>>({});
  const [constructedResponses, setConstructedResponses] = useState<Record<string, string>>({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<Record<string, boolean>>({});

  // Timing
  const totalSeconds = (mockExam.durationMinutes || 120) * 60;
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState<number>(totalSeconds);
  const [isTimeUp, setIsTimeUp] = useState<boolean>(false);
  const startTimeRef = useRef<number>(Date.now());

  // UI state
  const [isNavigatorOpen, setIsNavigatorOpen] = useState<boolean>(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [completionTimestamp, setCompletionTimestamp] = useState<string>('');
  const [scoreReport, setScoreReport] = useState<UnofficialScoreReport | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);

  // 1. Fetch locked items by exact IDs & initialize MockAttempt
  useEffect(() => {
    let isMounted = true;

    async function initSimulator() {
      try {
        const result = await loadMockExamContent(mockExam);
        if (!isMounted) return;

        if (result.success === true) {
          setQuestions(result.questions);
          setCrPrompts(result.constructedResponses);
          setLoadState('ready');

          // Create attempt in Firestore
          const newAttemptId = await createMockAttempt(
            userProfile.uid,
            mockExam.id,
            mockExam.paperId
          );
          if (isMounted) {
            setAttemptId(newAttemptId);
          }
        } else {
          setLoadState('data_error');
          setDataErrorDetail({
            missingId: result.missingId || 'unknown',
            itemType: result.itemType || 'question',
            message: result.message || 'Failed to load mock exam data.'
          });
        }
      } catch (err) {
        if (!isMounted) return;
        setLoadState('data_error');
        setDataErrorDetail({
          missingId: 'unknown',
          itemType: 'question',
          message: err instanceof Error ? err.message : 'Unknown error during mock exam resolution.'
        });
      }
    }

    initSimulator();

    return () => {
      isMounted = false;
    };
  }, [mockExam.id]);

  // Final Submission
  const handleFinalSubmit = async (autoSubmitted = false) => {
    setIsSubmitModalOpen(false);
    const nowIso = new Date().toISOString();
    const elapsedSeconds = Math.max(1, Math.round((Date.now() - startTimeRef.current) / 1000));

    const completedAttemptObj: MockAttempt = {
      id: attemptId,
      userId: userProfile.uid || 'guest_user_1',
      mockExamId: mockExam.id,
      paperId: mockExam.paperId,
      startedAt: new Date(startTimeRef.current).toISOString(),
      completedAt: nowIso,
      status: 'completed',
      userAnswers,
      constructedResponses,
      timeSpentSeconds: elapsedSeconds
    };

    if (attemptId) {
      await updateMockAttempt(attemptId, {
        status: 'completed',
        completedAt: nowIso,
        userAnswers,
        constructedResponses,
        timeSpentSeconds: elapsedSeconds
      });
    }

    setCompletionTimestamp(nowIso);
    setIsCompleted(true);
    setIsGeneratingReport(true);

    try {
      const generatedReport = await generateUnofficialScoreReport(completedAttemptObj, mockExam);
      setScoreReport(generatedReport);
    } catch (err) {
      console.error('Error generating unofficial score report:', err);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // 2. Countdown Timer
  useEffect(() => {
    if (loadState !== 'ready' || isCompleted) return;

    const timer = setInterval(() => {
      setTimeRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsTimeUp(true);
          handleFinalSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loadState, isCompleted, attemptId, userAnswers, constructedResponses]);

  // Handle MC Answer selection
  const handleSelectAnswer = (qId: string, optionIndex: number) => {
    setUserAnswers((prev) => {
      const updated = { ...prev, [qId]: optionIndex };
      // Periodically sync attempt updates in background
      if (attemptId) {
        updateMockAttempt(attemptId, {
          userAnswers: updated,
          timeSpentSeconds: Math.round((Date.now() - startTimeRef.current) / 1000)
        });
      }
      return updated;
    });
  };

  // Handle CR text update
  const handleCrTextChange = (promptId: string, text: string) => {
    setConstructedResponses((prev) => {
      const updated = { ...prev, [promptId]: text };
      if (attemptId) {
        updateMockAttempt(attemptId, {
          constructedResponses: updated,
          timeSpentSeconds: Math.round((Date.now() - startTimeRef.current) / 1000)
        });
      }
      return updated;
    });
  };

  // Toggle Flag
  const toggleFlag = (id: string) => {
    setFlaggedQuestions((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Format time (HH:MM:SS or MM:SS)
  const formatTime = (secs: number) => {
    const hrs = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Calculations
  const answeredMcCount = Object.keys(userAnswers).length;
  const answeredCrCount = Object.values(constructedResponses).filter((t: string) => t && t.trim().length > 0).length;
  const totalItemsCount = questions.length + crPrompts.length;
  const totalAnsweredCount = answeredMcCount + answeredCrCount;
  const progressPercent = totalItemsCount > 0 ? Math.round((totalAnsweredCount / totalItemsCount) * 100) : 0;
  const isTimeCritical = timeRemainingSeconds <= 300; // Under 5 minutes

  // ==========================================
  // VIEW: SUBSCRIPTION PAYWALL GATE
  // ==========================================
  const isSubscribed = isPaperUnlockedForUser(userProfile);
  if (!isSubscribed) {
    return (
      <div className="max-w-3xl mx-auto my-8 p-8 bg-white rounded-3xl border border-slate-200 shadow-sm space-y-6 text-center animate-in fade-in">
        <div className="inline-flex p-4 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200">
          <Lock className="w-10 h-10" />
        </div>
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 uppercase tracking-wide">
            Subscription Required
          </div>
          <h2 className="text-2xl font-black text-slate-900">
            Unlock Full Standardized Mock Exam Series
          </h2>
          <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
            Full-length timed mock exams with official domain score reports and constructed response evaluations require an active PraxisPrep Pro subscription.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          {openPricingModal && (
            <button
              onClick={openPricingModal}
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-sm transition shadow-sm flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4 text-amber-300" /> Unlock with Subscription
            </button>
          )}
          <button
            onClick={onExit}
            className="w-full sm:w-auto px-5 py-3 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-700 font-bold text-sm transition"
          >
            Back to Exams
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW: LOADING STATE
  // ==========================================
  if (loadState === 'loading') {
    return (
      <div className="min-h-[600px] flex flex-col items-center justify-center p-8 text-center space-y-4 bg-white rounded-3xl border border-slate-200 shadow-sm">
        <div className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 animate-pulse">
          <Clock className="w-7 h-7" />
        </div>
        <div className="space-y-1">
          <h2 className="text-xl font-black text-slate-900">
            Initializing {mockExam.seriesLabel} Mock Exam
          </h2>
          <p className="text-sm text-slate-500 max-w-md">
            Verifying and loading locked test items from the secure master collection...
          </p>
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW: DATA ERROR STATE (CRITICAL REQUIREMENT)
  // ==========================================
  if (loadState === 'data_error' && dataErrorDetail) {
    return (
      <div className="max-w-3xl mx-auto my-8 p-8 bg-white rounded-3xl border-2 border-red-200 shadow-xl space-y-6 text-left animate-in fade-in">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-red-100 border border-red-200 text-red-600 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-red-100 text-red-800 border border-red-200 uppercase tracking-wider">
              Data Integrity Error
            </div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">
              Mock Exam Data Resolution Failed
            </h2>
            <p className="text-sm text-slate-600">
              The locked mock exam series could not load because one or more referenced item IDs failed to resolve in the database.
            </p>
          </div>
        </div>

        <div className="bg-red-50/80 border border-red-200 rounded-2xl p-5 space-y-3 font-mono text-xs text-red-950">
          <div className="flex justify-between border-b border-red-200/60 pb-2">
            <span className="font-bold">Mock Exam ID:</span>
            <span>{mockExam.id}</span>
          </div>
          <div className="flex justify-between border-b border-red-200/60 pb-2">
            <span className="font-bold">Failed Item Type:</span>
            <span className="uppercase">{dataErrorDetail.itemType}</span>
          </div>
          <div className="flex justify-between border-b border-red-200/60 pb-2">
            <span className="font-bold">Missing Reference ID:</span>
            <span className="font-bold text-red-700">{dataErrorDetail.missingId}</span>
          </div>
          <div className="pt-1">
            <span className="font-bold block mb-1">Details:</span>
            <p className="font-sans text-xs text-red-900 leading-relaxed">
              {dataErrorDetail.message}
            </p>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-xs text-slate-600 flex items-start gap-2.5">
          <HelpCircle className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
          <p>
            This error indicates a strict data integrity guard triggered: mock exams are immutable and will never synthesize replacement questions on the fly. Please notify an administrator to re-index the bank.
          </p>
        </div>

        <div className="pt-2 flex justify-end">
          <button
            onClick={onExit}
            className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold transition flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Return to Exam Directory
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW: POST-SUBMISSION SCORE REPORT & EVALUATION
  // ==========================================
  if (isCompleted) {
    if (isGeneratingReport) {
      return (
        <div className="max-w-xl mx-auto my-16 bg-white rounded-3xl border border-slate-200 shadow-xl p-10 text-center space-y-6 animate-in fade-in">
          <div className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto animate-spin">
            <Sparkles className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-black text-slate-900">Computing Practice Evaluation...</h2>
            <p className="text-xs text-slate-500">
              Scoring multiple-choice items and compiling domain performance breakdown.
            </p>
          </div>
        </div>
      );
    }

    if (scoreReport) {
      return (
        <UnofficialScoreReportView
          report={scoreReport}
          mockExam={mockExam}
          onExit={onExit}
          onUpdateReport={(updated) => setScoreReport(updated)}
        />
      );
    }

    const elapsedMinutes = Math.round(
      (mockExam.durationMinutes * 60 - timeRemainingSeconds) / 60
    );

    return (
      <div className="max-w-3xl mx-auto my-8 bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 sm:p-10 text-center space-y-8 animate-in fade-in">
        <div className="w-20 h-20 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto border-4 border-emerald-50 shadow-inner">
          <CheckCircle2 className="w-10 h-10" />
        </div>

        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <Check className="w-3.5 h-3.5" /> Mock Exam Session Completed
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">
            Mock Exam Attempt Submitted
          </h1>
          <p className="text-sm text-slate-600 max-w-lg mx-auto">
            Your attempt for <strong className="text-slate-900">{mockExam.seriesLabel}</strong> has been securely logged to your student profile under Attempt ID:
            <code className="block mt-1 font-mono text-xs text-indigo-700 bg-indigo-50 py-1 px-2.5 rounded-lg border border-indigo-100 w-fit mx-auto">
              {attemptId}
            </code>
          </p>
        </div>

        {/* Summary Card */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-1">
            <div className="text-xs text-slate-500 font-semibold flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-indigo-600" /> Time Elapsed
            </div>
            <div className="text-2xl font-extrabold text-slate-900">
              {elapsedMinutes} mins
            </div>
            <p className="text-[11px] text-slate-400">
              Limit: {mockExam.durationMinutes} mins
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-1">
            <div className="text-xs text-slate-500 font-semibold flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Multiple Choice
            </div>
            <div className="text-2xl font-extrabold text-slate-900">
              {answeredMcCount} <span className="text-sm font-normal text-slate-500">/ {questions.length}</span>
            </div>
            <p className="text-[11px] text-slate-400">
              {questions.length - answeredMcCount} unanswered
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-1">
            <div className="text-xs text-slate-500 font-semibold flex items-center gap-1.5">
              <PenTool className="w-3.5 h-3.5 text-amber-600" /> Constructed Responses
            </div>
            <div className="text-2xl font-extrabold text-slate-900">
              {answeredCrCount} <span className="text-sm font-normal text-slate-500">/ {crPrompts.length}</span>
            </div>
            <p className="text-[11px] text-slate-400">
              {crPrompts.length - answeredCrCount} blank
            </p>
          </div>
        </div>

        {/* Status Confirmation */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-left text-xs text-slate-700 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <span className="font-bold text-slate-900 text-sm">
              Session Successfully Recorded
            </span>
            <p className="text-slate-600 leading-relaxed">
              Your raw responses have been persisted to <code className="font-mono text-indigo-900 font-bold">mockAttempts</code>.
            </p>
          </div>
        </div>

        <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={onExit}
            className="w-full sm:w-auto px-8 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs transition shadow-md flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Return to Exam Directory
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW: ACTIVE SIMULATOR SESSION
  // ==========================================
  const currentMcQuestion = questions[currentMcIndex];
  const currentCrPrompt = crPrompts[currentCrIndex];

  return (
    <div className="space-y-4 max-w-6xl mx-auto py-2">
      {/* Top Header & Sticky Navigation Bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onExit}
              className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
              title="Exit simulator"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-indigo-100 text-indigo-800 border border-indigo-200">
                  {mockExam.seriesLabel}
                </span>
                <span className="text-xs font-bold text-slate-700">
                  Standardized Mock Exam
                </span>
                <span className="text-xs text-slate-400 font-mono">
                  ({mockExam.paperId})
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Authentic testing conditions • Locked snapshot
              </p>
            </div>
          </div>

          {/* Center: Live Timer Clock */}
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-sm font-black transition-colors ${
                isTimeCritical
                  ? 'bg-red-50 text-red-700 border border-red-200 animate-pulse'
                  : 'bg-slate-900 text-white shadow-xs'
              }`}
            >
              <Clock className={`w-4 h-4 ${isTimeCritical ? 'text-red-600' : 'text-indigo-400'}`} />
              <span>{formatTime(timeRemainingSeconds)}</span>
            </div>

            <button
              onClick={() => setIsNavigatorOpen(!isNavigatorOpen)}
              className="px-3.5 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold transition flex items-center gap-1.5"
            >
              <Layers className="w-4 h-4 text-slate-500" />
              <span className="hidden sm:inline">Question</span> Navigator
            </button>

            <button
              onClick={() => setIsSubmitModalOpen(true)}
              className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-extrabold transition shadow-xs flex items-center gap-1.5"
            >
              <Send className="w-3.5 h-3.5" /> Submit Exam
            </button>
          </div>
        </div>

        {/* Section Tabs (MC vs CR) & Progress Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveSection('mc')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                activeSection === 'mc'
                  ? 'bg-indigo-600 text-white shadow-2xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200/70'
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Section 1: Multiple Choice ({questions.length})</span>
            </button>

            {crPrompts.length > 0 && (
              <button
                onClick={() => setActiveSection('cr')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                  activeSection === 'cr'
                    ? 'bg-indigo-600 text-white shadow-2xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200/70'
                }`}
              >
                <PenTool className="w-3.5 h-3.5" />
                <span>Section 2: Constructed Response ({crPrompts.length})</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto text-xs font-semibold text-slate-500">
            <span>
              {totalAnsweredCount} of {totalItemsCount} Answered ({progressPercent}%)
            </span>
            <div className="w-32 bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200 shrink-0">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Active Question / Prompt Area (Left 3 columns) */}
        <div className="lg:col-span-3 space-y-6">
          {activeSection === 'mc' && currentMcQuestion && (
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xs space-y-6">
              {/* Question Header */}
              <div className="flex items-center justify-between gap-4 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="w-8 h-8 rounded-xl bg-slate-900 text-white font-extrabold text-sm flex items-center justify-center">
                    {currentMcIndex + 1}
                  </span>
                  <span className="text-xs font-bold text-slate-500">
                    Question {currentMcIndex + 1} of {questions.length}
                  </span>
                  {currentMcQuestion.domain && (
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                      {currentMcQuestion.domain}
                    </span>
                  )}
                </div>

                <button
                  onClick={() => toggleFlag(currentMcQuestion.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
                    flaggedQuestions[currentMcQuestion.id]
                      ? 'bg-amber-100 text-amber-900 border border-amber-300'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                  }`}
                >
                  <Bookmark className={`w-3.5 h-3.5 ${flaggedQuestions[currentMcQuestion.id] ? 'fill-amber-600 text-amber-600' : ''}`} />
                  <span>{flaggedQuestions[currentMcQuestion.id] ? 'Flagged for Review' : 'Flag Question'}</span>
                </button>
              </div>

              {/* Question Text */}
              <div className="text-slate-900 text-base sm:text-lg font-medium leading-relaxed">
                {currentMcQuestion.questionText}
              </div>

              {/* Options */}
              <div className="space-y-3 pt-2">
                {currentMcQuestion.options?.map((option, optIdx) => {
                  const isSelected = userAnswers[currentMcQuestion.id] === optIdx;
                  const letter = String.fromCharCode(65 + optIdx);

                  return (
                    <div
                      key={optIdx}
                      onClick={() => handleSelectAnswer(currentMcQuestion.id, optIdx)}
                      className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-start gap-3.5 ${
                        isSelected
                          ? 'border-indigo-600 bg-indigo-50/50 shadow-xs ring-2 ring-indigo-500/20'
                          : 'border-slate-200 hover:border-slate-300 bg-slate-50/30 hover:bg-slate-50/70'
                      }`}
                    >
                      <div
                        className={`w-7 h-7 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 transition-colors ${
                          isSelected
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white border border-slate-300 text-slate-700'
                        }`}
                      >
                        {letter}
                      </div>
                      <span className={`text-sm leading-relaxed ${isSelected ? 'font-bold text-slate-900' : 'text-slate-700'}`}>
                        {option}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Navigation Footer */}
              <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                <button
                  disabled={currentMcIndex === 0}
                  onClick={() => setCurrentMcIndex((prev) => Math.max(0, prev - 1))}
                  className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 text-xs font-bold transition flex items-center gap-1.5"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>

                <div className="text-xs text-slate-400 font-medium">
                  Select one answer per question
                </div>

                {currentMcIndex < questions.length - 1 ? (
                  <button
                    onClick={() => setCurrentMcIndex((prev) => Math.min(questions.length - 1, prev + 1))}
                    className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold transition flex items-center gap-1.5 shadow-2xs"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                ) : crPrompts.length > 0 ? (
                  <button
                    onClick={() => {
                      setActiveSection('cr');
                      setCurrentCrIndex(0);
                    }}
                    className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-xs"
                  >
                    Proceed to Section 2 (CR) <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => setIsSubmitModalOpen(true)}
                    className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-xs"
                  >
                    Review & Submit <Send className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {activeSection === 'cr' && currentCrPrompt && (
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xs space-y-6">
              {/* CR Header */}
              <div className="flex items-center justify-between gap-4 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="w-8 h-8 rounded-xl bg-amber-600 text-white font-extrabold text-sm flex items-center justify-center">
                    CR {currentCrIndex + 1}
                  </span>
                  <span className="text-xs font-bold text-slate-700">
                    Constructed Response {currentCrIndex + 1} of {crPrompts.length}
                  </span>
                  {currentCrPrompt.domain && (
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-900 border border-amber-200">
                      {currentCrPrompt.domain}
                    </span>
                  )}
                </div>

                <button
                  onClick={() => toggleFlag(currentCrPrompt.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
                    flaggedQuestions[currentCrPrompt.id]
                      ? 'bg-amber-100 text-amber-900 border border-amber-300'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                  }`}
                >
                  <Bookmark className={`w-3.5 h-3.5 ${flaggedQuestions[currentCrPrompt.id] ? 'fill-amber-600 text-amber-600' : ''}`} />
                  <span>{flaggedQuestions[currentCrPrompt.id] ? 'Flagged' : 'Flag Prompt'}</span>
                </button>
              </div>

              {/* CR Prompt Text Box */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-indigo-600" /> Prompt Scenario & Instructions
                </div>
                <div className="text-slate-900 text-sm sm:text-base leading-relaxed font-medium">
                  {currentCrPrompt.promptText}
                </div>
              </div>

              {/* Rubric Notes */}
              {currentCrPrompt.rubricNotes && (
                <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-3.5 text-xs text-amber-950 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-bold">Holistic Rubric Focus:</span> {currentCrPrompt.rubricNotes}
                  </div>
                </div>
              )}

              {/* Student Response Text Area */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                  <span>Your Written Response:</span>
                  <span>
                    Word Count:{' '}
                    <strong className="text-slate-900">
                      {(constructedResponses[currentCrPrompt.id] || '').trim().split(/\s+/).filter(Boolean).length}
                    </strong>{' '}
                    words (Target: ~150-200)
                  </span>
                </div>

                <textarea
                  value={constructedResponses[currentCrPrompt.id] || ''}
                  onChange={(e) => handleCrTextChange(currentCrPrompt.id, e.target.value)}
                  placeholder="Type your constructed response analysis here. Reference pedagogical strategies, standards, and literary/content details..."
                  rows={10}
                  className="w-full bg-slate-50/50 border border-slate-300 focus:border-indigo-600 focus:bg-white rounded-2xl p-4 text-sm text-slate-900 leading-relaxed placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 font-sans"
                />
              </div>

              {/* Navigation Footer */}
              <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                <button
                  onClick={() => {
                    if (currentCrIndex > 0) {
                      setCurrentCrIndex((prev) => prev - 1);
                    } else {
                      setActiveSection('mc');
                      setCurrentMcIndex(questions.length - 1);
                    }
                  }}
                  className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold transition flex items-center gap-1.5"
                >
                  <ChevronLeft className="w-4 h-4" /> {currentCrIndex === 0 ? 'Back to Section 1 (MC)' : 'Previous CR'}
                </button>

                {currentCrIndex < crPrompts.length - 1 ? (
                  <button
                    onClick={() => setCurrentCrIndex((prev) => prev + 1)}
                    className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold transition flex items-center gap-1.5 shadow-2xs"
                  >
                    Next CR <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => setIsSubmitModalOpen(true)}
                    className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-xs"
                  >
                    Review & Submit Exam <Send className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar: Navigator & Progress */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-3xl border border-slate-200 p-5 shadow-xs space-y-4 sticky top-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-indigo-600" /> Exam Navigator
              </h3>
              <span className="text-[11px] font-bold text-slate-400">
                {totalAnsweredCount}/{totalItemsCount}
              </span>
            </div>

            {/* Section 1 MC Grid */}
            <div className="space-y-2">
              <div className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                <span>Section 1: MC ({answeredMcCount}/{questions.length})</span>
              </div>
              <div className="grid grid-cols-5 gap-1.5 max-h-56 overflow-y-auto pr-1">
                {questions.map((q, idx) => {
                  const isAnswered = userAnswers[q.id] !== undefined;
                  const isCurrent = activeSection === 'mc' && currentMcIndex === idx;
                  const isFlagged = flaggedQuestions[q.id];

                  return (
                    <button
                      key={q.id}
                      onClick={() => {
                        setActiveSection('mc');
                        setCurrentMcIndex(idx);
                      }}
                      className={`h-8 rounded-lg text-xs font-bold transition flex items-center justify-center relative ${
                        isCurrent
                          ? 'ring-2 ring-indigo-600 bg-indigo-600 text-white'
                          : isAnswered
                          ? 'bg-indigo-50 border border-indigo-200 text-indigo-900 hover:bg-indigo-100'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                      }`}
                    >
                      <span>{idx + 1}</span>
                      {isFlagged && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500 border border-white" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Section 2 CR Grid */}
            {crPrompts.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <div className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                  Section 2: CR ({answeredCrCount}/{crPrompts.length})
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {crPrompts.map((cr, idx) => {
                    const isWritten = (constructedResponses[cr.id] || '').trim().length > 0;
                    const isCurrent = activeSection === 'cr' && currentCrIndex === idx;
                    const isFlagged = flaggedQuestions[cr.id];

                    return (
                      <button
                        key={cr.id}
                        onClick={() => {
                          setActiveSection('cr');
                          setCurrentCrIndex(idx);
                        }}
                        className={`h-8 rounded-lg text-xs font-bold transition flex items-center justify-center relative ${
                          isCurrent
                            ? 'ring-2 ring-amber-600 bg-amber-600 text-white'
                            : isWritten
                            ? 'bg-amber-50 border border-amber-200 text-amber-900'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                      >
                        <span>CR {idx + 1}</span>
                        {isFlagged && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500 border border-white" />
                        )}
                      </button>
                    )}
                  )}
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="pt-3 border-t border-slate-100 space-y-1.5 text-[10px] text-slate-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-indigo-50 border border-indigo-200 shrink-0" />
                <span>Answered Item</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-slate-100 shrink-0" />
                <span>Unanswered</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />
                <span>Flagged for Review</span>
              </div>
            </div>

            <button
              onClick={() => setIsSubmitModalOpen(true)}
              className="w-full mt-2 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-extrabold transition shadow-xs flex items-center justify-center gap-1.5"
            >
              <Send className="w-3.5 h-3.5" /> Submit Exam
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Submit Modal */}
      {isSubmitModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-200 space-y-6 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h3 className="text-xl font-black text-slate-900">
                  Ready to Submit Your Exam?
                </h3>
                <p className="text-xs text-slate-500 font-medium">
                  {mockExam.seriesLabel} • {mockExam.paperId}
                </p>
              </div>
              <button
                onClick={() => setIsSubmitModalOpen(false)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status overview */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2.5 text-xs">
              <div className="flex justify-between font-semibold">
                <span className="text-slate-600">Multiple Choice Questions:</span>
                <span className="text-slate-900 font-bold">
                  {answeredMcCount} of {questions.length} answered
                </span>
              </div>

              {crPrompts.length > 0 && (
                <div className="flex justify-between font-semibold">
                  <span className="text-slate-600">Constructed Response Prompts:</span>
                  <span className="text-slate-900 font-bold">
                    {answeredCrCount} of {crPrompts.length} completed
                  </span>
                </div>
              )}

              <div className="flex justify-between font-semibold border-t border-slate-200 pt-2">
                <span className="text-slate-600">Time Remaining:</span>
                <span className="font-mono text-indigo-700 font-bold">
                  {formatTime(timeRemainingSeconds)}
                </span>
              </div>
            </div>

            {totalAnsweredCount < totalItemsCount && (
              <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-950 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <span>
                  You have <strong>{totalItemsCount - totalAnsweredCount}</strong> unanswered items. You can still return to complete them before the time expires.
                </span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setIsSubmitModalOpen(false)}
                className="px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-xs transition"
              >
                Return to Exam
              </button>
              <button
                onClick={() => handleFinalSubmit(false)}
                className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs transition shadow-md flex items-center gap-1.5"
              >
                <Check className="w-4 h-4" /> Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
